import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);

  const services = [
    {
      id: 'standard',
      icon: '🧹',
      title: 'Standard Carpet Cleaning',
      description: 'Perfect for regular maintenance and light soil removal',
      fullDescription: 'Our standard cleaning service uses eco-friendly solutions to remove dirt, dust, and light stains from your carpets. Ideal for regular maintenance and keeping your carpets looking fresh.',
      price: '$3.50/sq ft',
      baseRate: 3.50,
      features: [
        'Eco-friendly steam cleaning',
        'Light stain treatment',
        'Deodorizing treatment',
        '2-4 hour drying time',
        'Pet-friendly solutions'
      ],
      bestFor: ['Regular maintenance', 'Light soiling', 'Rental properties'],
      image: '/images/service1.jpg',
      popular: false
    },
    {
      id: 'premium',
      icon: '🌟',
      title: 'Premium Deep Cleaning',
      description: 'Intensive cleaning for heavily soiled carpets and stubborn stains',
      fullDescription: 'Our premium service tackles tough stains, deep-seated dirt, and heavy soiling. Using advanced equipment and specialized solutions, we restore even the most worn carpets.',
      price: '$4.25/sq ft',
      baseRate: 4.25,
      features: [
        'Advanced steam extraction',
        'Pre-treatment for tough stains',
        'Sanitizing treatment',
        'Stain protection application',
        '1-3 hour drying time',
        'Allergen reduction'
      ],
      bestFor: ['Heavy traffic areas', 'Pet stains', 'Old stains', 'High-end homes'],
      image: '/images/service2.jpg',
      popular: true
    },
    {
      id: 'stain-removal',
      icon: '💧',
      title: 'Stain Removal Service',
      description: 'Specialized treatment for specific stains and spot cleaning',
      fullDescription: 'Targeted stain removal for wine, coffee, pet accidents, and other stubborn spots. We use specialized solutions and techniques to eliminate even the toughest stains.',
      price: '$99 per room',
      baseRate: 99,
      features: [
        'Specialized stain identification',
        'Targeted treatment',
        'Odor elimination',
        'Color-safe solutions',
        'Quick drying',
        'Spot treatment guarantee'
      ],
      bestFor: ['Wine spills', 'Pet accidents', 'Coffee stains', 'Ink marks'],
      image: '/images/service3.jpg',
      popular: false
    }
  ];

  const stats = [
    { number: '24/7', label: 'Emergency Service' },
    { number: '2H', label: 'Avg Response Time' },
    { number: '99%', label: 'Satisfaction Guarantee' },
    { number: '6+', label: 'Years Experience' }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Consultation',
      description: 'We assess your needs and provide a detailed quote'
    },
    {
      step: '02',
      title: 'Preparation',
      description: 'Our team prepares the area and applies pre-treatment'
    },
    {
      step: '03',
      title: 'Deep Cleaning',
      description: 'Advanced equipment removes dirt and stains'
    },
    {
      step: '04',
      title: 'Finishing',
      description: 'Final treatments and quality check'
    }
  ];

  const openServiceDetail = (service) => {
    setSelectedService(service);
  };

  const closeServiceDetail = () => {
    setSelectedService(null);
  };

  return (
    <>
      <SEO 
        title="Carpet Cleaning Services - Standard, Premium & Emergency"
        description="Comprehensive carpet cleaning services including standard cleaning, premium deep cleaning, stain removal, move-in/move-out, and commercial cleaning. Eco-friendly solutions."
        keywords="carpet cleaning services, deep cleaning, stain removal, commercial cleaning, move-in cleaning, emergency cleaning"
        url="https://greenrockycarpet.com.au/services"
        section="Services"
      />

    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">Our Professional Services</h1>
            <p className="hero-subtitle">
              Experience the difference with our comprehensive carpet cleaning solutions. 
              From regular maintenance to emergency services, we've got you covered.
            </p>
            <div className="hero-stats">
              {stats.map((stat, index) => (
                <div key={index} className="hero-stat">
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-grid-section">
        <div className="container">
          <div className="section-header">
            <div className="section-tag">Our Services</div>
            <h2 className="section-title">Comprehensive Cleaning Solutions</h2>
            <p className="section-subtitle">
              Choose from our range of professional services tailored to meet your specific needs
            </p>
          </div>

          <div className="services-grid">
            {services.map((service, index) => (
              <div 
                key={service.id} 
                className={`service-card ${service.popular ? 'popular' : ''}`}
              >
                {service.popular && (
                  <div className="popular-badge">Most Popular</div>
                )}
                
                <div className="service-header">
                  <div className="service-icon">{service.icon}</div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                </div>

                <div className="service-image">
                  <img src={service.image} alt={service.title} />
                </div>

                <div className="service-features">
                  <h4>What's Included:</h4>
                  <ul>
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx}>
                        <span className="check-icon">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="service-bestfor">
                  <h4>Best For:</h4>
                  <div className="bestfor-tags">
                    {service.bestFor.map((item, idx) => (
                      <span key={idx} className="bestfor-tag">{item}</span>
                    ))}
                  </div>
                </div>

                <div className="service-footer">
                  <div className="service-price">
                    <span className="price">{service.price}</span>
                    <span className="price-note">Minimum charge may apply</span>
                  </div>
                  <div className="service-actions">
                    <button 
                      className="btn-secondary"
                      onClick={() => openServiceDetail(service)}
                    >
                      Learn More
                    </button>
                    <Link 
                      to="/estimation" 
                      state={{ serviceType: service.id }}
                      className="btn-primary"
                    >
                      Get Estimate
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="container">
          <div className="section-header">
            <div className="section-tag">Our Process</div>
            <h2 className="section-title">How We Deliver Excellence</h2>
            <p className="section-subtitle">
              Our proven 4-step process ensures consistent, high-quality results every time
            </p>
          </div>
          <div className="process-steps">
            {processSteps.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{step.step}</div>
                <div className="step-content">
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="step-connector"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Transform Your Space?</h2>
            <p className="cta-description">
              Get started with a free, no-obligation estimate. Our team is ready to make your carpets look brand new.
            </p>
            <div className="cta-buttons">
              <Link to="/estimation" className="btn-primary large">
                Get Free Estimate
              </Link>
              <Link to="/contact" className="btn-secondary2 large">
                Contact Us
              </Link>
            </div>
            <div className="cta-features">
              <div className="cta-feature">
                <span className="feature-icon">🎯</span>
                Free On-site Estimates
              </div>
              <div className="cta-feature">
                <span className="feature-icon">⚡</span>
                Same Day Service Available
              </div>
              <div className="cta-feature">
                <span className="feature-icon">🛡️</span>
                100% Satisfaction Guarantee
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Detail Modal */}
      {selectedService && (
        <div className="service-modal">
          <div className="modal-backdrop" onClick={closeServiceDetail}></div>
          <div className="modal-content">
            <button className="modal-close" onClick={closeServiceDetail}>
              ×
            </button>
            
            <div className="modal-header">
              <div className="service-icon-large">{selectedService.icon}</div>
              <div className="modal-title">
                <h2>{selectedService.title}</h2>
                <p>{selectedService.description}</p>
              </div>
              <div className="modal-price">
                {selectedService.price}
              </div>
            </div>

            <div className="modal-body">
              <div className="modal-section">
                <h3>Service Overview</h3>
                <p>{selectedService.fullDescription}</p>
              </div>

              <div className="modal-grid">
                <div className="modal-section">
                  <h3>What's Included</h3>
                  <ul className="features-list">
                    {selectedService.features.map((feature, idx) => (
                      <li key={idx}>
                        <span className="check-icon">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="modal-section">
                  <h3>Best For</h3>
                  <div className="bestfor-tags">
                    {selectedService.bestFor.map((item, idx) => (
                      <span key={idx} className="bestfor-tag">{item}</span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h3>Service Image</h3>
                <img 
                  src={selectedService.image} 
                  alt={selectedService.title}
                  className="modal-image"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeServiceDetail}>
                Close
              </button>
              <Link 
                to="/estimation" 
                state={{ serviceType: selectedService.id }}
                className="btn-primary"
                onClick={closeServiceDetail}
              >
                Get Estimate for This Service
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  </>
  );
};

export default Services;